import { useEffect, useState } from 'react'
import { CircleCheck, CircleX, Clock, ShieldQuestion } from 'lucide-react'
import { renderAgentMarkdown } from '@/components/agent-markdown/renderAgentMarkdown'
import { Button } from '@/components/ds2'
import { isAnswerable, type ToolQuestion, type ToolQuestionStatus } from '../../hooks/toolQuestions'

interface ToolQuestionCardProps {
    question: ToolQuestion
    onAnswer: (action: 'accept' | 'decline') => void
}

// What the card says once it can no longer be answered.
const CLOSED_TEXT: Partial<Record<ToolQuestionStatus, string>> = {
    accepted: 'You approved this.',
    declined: 'You declined this. Nothing was done.',
    answered: 'Answered in another window.',
    withdrawn: 'Your new message replaced this question. Nothing was done.',
    expired: 'Expired, nothing was done.',
    // A 404 or a question the server no longer lists: it may have been answered elsewhere, so no outcome is claimed.
    gone: "This question is no longer waiting. The agent's reply will say what happened.",
}

/** Minutes and seconds left, as `m:ss`. */
const formatLeft = (ms: number): string => {
    const seconds = Math.max(0, Math.ceil(ms / 1000))
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/** Ticks once a second while the card can still be answered, so the countdown runs without a server event. */
const useMsLeft = (expiresAt: string, ticking: boolean): number => {
    const deadline = Date.parse(expiresAt)
    const [now, setNow] = useState(() => Date.now())
    useEffect(() => {
        if (!ticking) return
        const timer = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(timer)
    }, [ticking])
    return deadline - now
}

/**
 * The MCP kit ends every approval question with how to answer its form ("To approve, tick … and accept."), for clients that draw
 * that form. This card answers with Yes and No instead, so the line would name a box that is not there.
 */
const FORM_FOOTER = /\s*To approve, tick "[^"]+" and accept\.\s*$/

const withoutFormFooter = (message: string): string => message.replace(FORM_FOOTER, '')

/** A question holding a Markdown table (one row per invoice, say): drawn the thread's full width, so its columns fit. */
const hasTable = (message: string): boolean => /^\s*\|.*\|\s*$/m.test(message)

/**
 * A card's question in the thread (ADR-048 decisions 9-11): the card's own message word for word (Markdown, never HTML),
 * Yes and No, and the time left to answer. The card decides what the write does; this page only answers it.
 */
export const ToolQuestionCard = ({ question, onAnswer }: ToolQuestionCardProps) => {
    const answerable = question.status === 'open' || question.status === 'sending'
    const msLeft = useMsLeft(question.expiresAt, answerable)
    // Expiry is the server's to decide (its event or a 410): this page's clock may be off, so a card the countdown has run
    // out on stays answerable and only stops showing the time.
    const status: ToolQuestionStatus = question.status
    const closedText = CLOSED_TEXT[status]
    // A new message withdraws an open question on the server: Yes and No stop being offered at once, not when it confirms.
    const canAnswer = isAnswerable(question)
    const ClosedIcon = status === 'accepted' ? CircleCheck : CircleX

    return (
        <section
            aria-label="Approval needed"
            className={`flex ${hasTable(question.cardMessage) ? 'w-full' : 'max-w-[80%]'} flex-col gap-3 self-start rounded-2xl bg-surface p-4 text-sm text-foreground shadow-surface`}
        >
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                <ShieldQuestion className="h-4 w-4" aria-hidden /> Approval needed
            </div>
            <div className="break-words">{renderAgentMarkdown(withoutFormFooter(question.cardMessage))}</div>
            {closedText ? (
                <p className="flex items-center gap-2 text-foreground-muted" role="status">
                    <ClosedIcon className="h-4 w-4" aria-hidden /> {closedText}
                </p>
            ) : (
                <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" loading={status === 'sending'} disabled={!canAnswer} onClick={() => onAnswer('accept')}>
                        Yes
                    </Button>
                    <Button size="sm" variant="secondary" disabled={!canAnswer} onClick={() => onAnswer('decline')}>
                        No
                    </Button>
                    {msLeft > 0 && (
                        <span className="ml-auto flex items-center gap-1 text-xs text-foreground-muted" aria-label="Time left to answer">
                            <Clock className="h-3.5 w-3.5" aria-hidden /> {formatLeft(msLeft)}
                        </span>
                    )}
                </div>
            )}
            {question.error && status === 'open' && (
                <p className="text-danger-soft-foreground" role="alert">
                    {question.error}
                </p>
            )}
        </section>
    )
}
